import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { View } from 'react-native';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { rem } from 'design-system/values';
import ScrollView from 'design-system/ScrollView';
import { SettingsItem } from 'design-system/SettingsItem';
import IconPlus from 'design-system/icons/IconPlus';
import Squircle from 'design-system/Squircle';
import FeatureIntroduction from 'design-system/FeatureIntroduction';
import IconSpace from 'design-system/icons/IconSpace';
import { IconTwoUser } from 'design-system/icons/IconTwoUser';
import { IconShare } from 'design-system/icons/IconShare';
import { IconSettings } from 'design-system/icons/IconSettings';
import { Indicator } from 'design-system/Indicator';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import { useSharedValue } from 'react-native-reanimated';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import Links from 'utils/links';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import TouchableBounceCard from 'utils/packages/TouchableBounceCard';
import SectionHeader from 'design-system/SectionHeader';
import { HelpCallToAction, TextButtonLink } from 'design-system';
import { selectUserId } from 'reducers/selectors';
import { setSelectedSpace } from '../actions';
import { SpaceItem } from '../components';
import { selectSelectedSpace } from '../reducers/selectors';
import { isWeb } from '../../../constants';
import { useJoinSpaceModal } from '../hooks/useJoinSpaceModal';
const SPACES_INTRO_ITEMS = [
    {
        icon: <IconTwoUser gradientKey="darkPink"/>,
        title: 'Group',
        description: 'Group your accounts in Spaces and set different budgets',
    },
    {
        icon: <IconSettings gradientKey="orange"/>,
        title: 'Manage',
        description: 'Manage your personal, joint and business accounts all in Emma.',
    },
    {
        icon: <IconShare gradientKey="skyBlue"/>,
        title: 'Share',
        description: 'Share Spaces with your partner(s) and allow them to use Pro features such as Custom Categories.',
    },
];
const strings = {
    title: 'Introducing spaces',
    subTitle: 'Great for business owners, self-employed and families',
    infoText: 'Curious about how Spaces can help you?\n',
    continueButton: 'Continue',
};
const MySpacesPage = ({ spaces, isLoading }) => {
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const styles = useStyles(styleSet);
    const colors = useColors();
    const scrollY = useSharedValue(0);
    const hasSpaces = spaces && spaces.length > 1;
    const onJoinSpace = useJoinSpaceModal();
    useLayoutEffect(() => {
        navigation.setOptions({
            title: hasSpaces ? 'Switch space' : '',
        });
    }, [hasSpaces]);
    const userId = useAppSelector(selectUserId);
    const spaceSelection = useAppSelector(selectSelectedSpace);
    // for FE radio button UI only
    const [selectedSpaceId, setSelectedSpaceId] = useState(spaceSelection);
    const [topSpaceId] = useState(spaceSelection);
    const selectedSpace = spaces?.find((space) => space.id === selectedSpaceId);
    const onChangeSpace = useCallback(() => {
        if (selectedSpace === undefined) {
            return;
        }
        if (selectedSpace.isDefault || selectedSpace.isPremium) {
            dispatch(setSelectedSpace(selectedSpace.id));
            navigation.navigateDeprecated('Success', {
                topTitle: 'App changed to:',
                title: `${selectedSpace.isDefault ? 'Private' : selectedSpace.name} space`,
                subTitle: 'Only assets that you have in this space will be visible in Feed.',
                buttonText: 'Done',
                onContinue: () => {
                    navigation.popToTop();
                    navigation.navigateDeprecated('Feed');
                    return true;
                },
            });
        }
    }, [selectedSpace]);
    const onSelect = useCallback((space) => {
        if (!space.isDefault && !space.isPremium) {
            if (space.createdBy === userId) {
                navigation.navigateDeprecated('EditSpace', { id: space.id, space });
            }
            else {
                navigation.navigateDeprecated('SpaceUnavailable', { id: space.id });
            }
        }
        else {
            setSelectedSpaceId(space.id);
        }
    }, [userId]);
    const onManageSpaces = useCallback(() => {
        if (spaces)
            navigation.navigateDeprecated('ManageSpaces');
    }, [spaces]);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING);
    const onHelpPress = useCallback(() => {
        Links.openKnownLink('spacesHelp');
    }, []);
    const reorderedSpaces = useMemo(() => {
        if (!spaces)
            return [];
        const currentSpace = spaces?.find((space) => space.id === topSpaceId);
        if (currentSpace === undefined)
            return spaces;
        return [
            currentSpace,
            ...spaces.filter((item) => item.id !== currentSpace.id),
        ];
    }, [spaces, topSpaceId]);
    const onFindAccounts = useCallback(() => {
        navigation.navigateDeprecated('FindSpaceAccounts');
    }, []);
    if (isLoading && !hasSpaces) {
        return (<View style={styles.container}>
        {isLoading && !hasSpaces ? (<View testID="my-spaces-loader" style={styles.loader}>
            <Indicator dark/>
          </View>) : null}
      </View>);
    }
    return (<View style={styles.container}>
      {hasSpaces ? (<ScrollView secondary scrollY={scrollY} contentContainerStyle={contentContainerStyle}>
          <SectionHeader button={<TextButtonLink onPress={onManageSpaces} text="Manage"/>} style={styles.sectionHeader}>
            Spaces
          </SectionHeader>
          <View style={styles.listContainer}>
            <SettingsItem onPress={onJoinSpace} title="Add new space" renderIcon={() => (<View style={styles.addNewSpace}>
                  <Squircle width={rem(44)} height={rem(44)} color={colors.background.dark}>
                    <IconPlus gradientKey="ultimate"/>
                  </Squircle>
                </View>)}/>
            {reorderedSpaces.map((item) => (<React.Fragment key={item.id}>
                <SpaceItem space={item} isSelected={(spaceSelection === undefined && item.isDefault) ||
                    item.id === spaceSelection} selectedSpaceId={selectedSpaceId} onSelect={onSelect}/>
              </React.Fragment>))}
          </View>
          <TouchableBounceCard noMarginBottom onDark onPress={onFindAccounts}>
            <View pointerEvents="none" style={styles.findYourAccounts}>
              <HelpCallToAction alt onDark onPress={noop} title="Can’t find your accounts?" subTitle="See which Spaces they’re in"/>
            </View>
          </TouchableBounceCard>
        </ScrollView>) : (<FeatureIntroduction title={strings.title} subTitle={strings.subTitle} items={SPACES_INTRO_ITEMS} buttonText="Join a space" onPressNext={onJoinSpace} topIcon={<IconSpace colorKey="brand"/>} infoCard={{ onPressInfo: onHelpPress, infoText: strings.infoText }}/>)}
      {selectedSpaceId &&
            selectedSpaceId !== spaceSelection &&
            (selectedSpace?.isDefault || selectedSpace?.name) ? (<Button title={strings.continueButton} floating brand onPress={onChangeSpace}/>) : null}
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    container: {
        width: '100%',
        flex: 1,
        alignItems: 'stretch',
    },
    listContainer: {
        backgroundColor: colors.cards.onDark,
        marginHorizontal: rem(16),
        borderRadius: rem(16),
        ...(isWeb && {
            padding: 8,
        }),
    },
    loader: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    addNewSpace: {
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: rem(16),
    },
    sectionHeader: {
        marginHorizontal: rem(16),
        marginBottom: rem(16),
    },
    findYourAccounts: {
        ...(isWeb && {
            paddingHorizontal: 8,
        }),
    },
}));
export default MySpacesPage;
